/**
 * VecchioMetodo — confronto onesto "vecchio metodo vs Pronto".
 *
 * iter v31 r54 — User: «Spunto: tabella Pronto vs vecchio metodo per
 * convincere gli agenti scettici. Fallo dopo il Bento, prima del pricing».
 *
 * Regole copy:
 *  - Niente percentuali o stime inventate ("5x più veloce", "risparmi 80%").
 *  - Solo confronti concreti e verificabili (cosa fai oggi vs cosa fai con
 *    Pronto), basati su feature realmente esistenti nel backend.
 *  - Tono umile, descrittivo, non aggressivo verso i competitor.
 */
import React from "react";
import {
  Camera,
  Browsers,
  InstagramLogo,
  Microphone,
  Calculator,
  Clock,
  Lightning,
} from "@phosphor-icons/react";

const ROWS = [
  {
    id: "portali",
    icon: Browsers,
    job: "Mettere lo stesso annuncio su più portali",
    before:
      "Apri Idealista, scrivi titolo, descrizione, prezzo, caratteristiche. Apri Immobiliare.it, riscrivi. Apri Casa.it, riscrivi.",
    after:
      "Crei l'annuncio una volta dentro Pronto: lo stesso lavoro va su Idealista, Immobiliare e nella tua vetrina personale. Il click finale «Pubblica» resta tuo.",
  },
  {
    id: "foto",
    icon: Camera,
    job: "Avere foto presentabili per la pubblicazione",
    before:
      "Chiami un fotografo professionista, paghi a immobile, aspetti i provini. Per gli appartamenti vuoti o disordinati, niente.",
    after:
      "Le tue foto migliorate (luce, ordine), arredo virtuale per stanze vuote, stanze svuotate per stanze affollate, pianta arredata. 1 credito a foto, pronte in 30 secondi.",
  },
  {
    id: "social",
    icon: InstagramLogo,
    job: "Un post Instagram per un nuovo annuncio",
    before:
      "Apri Canva, cerchi un template, sistemi colori e font, esporti, scarichi, carichi su Instagram. Ripeti per ogni formato (post, story, reel).",
    after:
      "Apri l'annuncio, premi «Editor social». Carosello, story 9:16, reel Prima/Dopo e caption sono già col tuo brand e i tuoi colori. Scarichi e pubblichi.",
  },
  {
    id: "sopralluogo",
    icon: Microphone,
    job: "Appunti del sopralluogo + bozza descrizione",
    before:
      "Scrivi a penna sul taccuino. La sera ricopi tutto al computer. Il giorno dopo provi a montare la descrizione dell'annuncio.",
    after:
      "Mentre giri la casa, parli al telefono come fosse un memo vocale. Pronto trascrive, separa stanza per stanza, ti propone una bozza di descrizione pulita.",
  },
  {
    id: "stima",
    icon: Calculator,
    job: "Stima per un proprietario incuriosito",
    before:
      "Il proprietario chiama, fissi l'appuntamento, vai sul posto, prendi misure, controlli i comparabili, rimandi la stima la sera. A volte non si fa più sentire.",
    after:
      "Gli mandi il tuo link personale. Lui risponde a 6 domande dal telefono, vede subito un range. Se vuole il dettaglio (cedolare, comparabili) ti lascia il contatto. Lead in rubrica.",
  },
];

export default function VecchioMetodo() {
  return (
    <section
      id="vecchio-metodo"
      className="py-16 sm:py-24 bg-[#FAFAF7]"
      data-testid="feature-vecchio-metodo"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        {/* Header */}
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="uppercase text-[11px] tracking-[0.22em] font-bold text-[#7C8B6E]">
            Stesso lavoro · meno passaggi
          </span>
          <h2 className="font-editorial text-[30px] sm:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight text-[#1C1C1A]">
            Quello che oggi fai a mano,{" "}
            <span className="italic text-[#7C8B6E]">qui lo fai in un click.</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-[16px] text-[#6B6A65] leading-relaxed">
            Niente sostituzioni: gli annunci li scegli tu, le foto le scatti tu,
            la trattativa è tua. Cambia solo il numero di programmi aperti
            contemporaneamente.
          </p>
        </div>

        {/* Desktop: tabella 3 colonne (Cosa fai · Vecchio · Pronto) */}
        <div
          className="hidden md:block bg-white rounded-2xl border border-[#E5E7E0] overflow-hidden shadow-[0_2px_24px_-12px_rgba(28,28,26,0.08)]"
          data-testid="vecchio-metodo-table"
        >
          {/* Header row */}
          <div className="grid grid-cols-12 gap-0 bg-[#F1F4ED] border-b border-[#E5E7E0]">
            <div className="col-span-3 px-5 py-3.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D]">
              Cosa fai
            </div>
            <div className="col-span-4 px-5 py-3.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B] inline-flex items-center gap-1.5">
              <Clock size={13} weight="bold" /> Il vecchio metodo
            </div>
            <div className="col-span-5 px-5 py-3.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] inline-flex items-center gap-1.5">
              <Lightning size={13} weight="fill" /> Con Pronto
            </div>
          </div>

          {ROWS.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.id}
                className="grid grid-cols-12 gap-0 border-b border-[#EDEAE2] last:border-b-0"
                data-testid={`vecchio-row-${r.id}`}
              >
                <div className="col-span-3 px-5 py-5 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F1F4ED] flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={18} weight="regular" className="text-[#7C8B6E]" />
                  </div>
                  <div className="font-editorial text-[15px] sm:text-[16.5px] text-[#1C1C1A] leading-snug pt-1">
                    {r.job}
                  </div>
                </div>
                <div className="col-span-4 px-5 py-5 text-[13.5px] text-[#6B6A65] leading-relaxed border-l border-[#EDEAE2]">
                  {r.before}
                </div>
                <div className="col-span-5 px-5 py-5 text-[13.5px] text-[#1C1C1A] leading-relaxed bg-[#FAFBF7] border-l border-[#EDEAE2]">
                  {r.after}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: card singole impilate, ogni card mostra Vecchio + Pronto */}
        <div className="md:hidden space-y-4" data-testid="vecchio-metodo-cards">
          {ROWS.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.id}
                className="bg-white rounded-2xl border border-[#E5E7E0] overflow-hidden"
                data-testid={`vecchio-card-${r.id}`}
              >
                <div className="px-5 pt-5 pb-3 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F1F4ED] flex items-center justify-center shrink-0">
                    <Icon size={20} weight="regular" className="text-[#7C8B6E]" />
                  </div>
                  <div className="font-editorial text-[18px] text-[#1C1C1A] leading-tight pt-1">
                    {r.job}
                  </div>
                </div>
                <div className="px-5 py-3.5 border-t border-[#EDEAE2]">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1.5 inline-flex items-center gap-1.5">
                    <Clock size={12} weight="bold" /> Il vecchio metodo
                  </div>
                  <p className="text-[13px] text-[#6B6A65] leading-relaxed">
                    {r.before}
                  </p>
                </div>
                <div className="px-5 py-3.5 bg-[#FAFBF7] border-t border-[#EDEAE2]">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1.5 inline-flex items-center gap-1.5">
                    <Lightning size={12} weight="fill" /> Con Pronto
                  </div>
                  <p className="text-[13px] text-[#1C1C1A] leading-relaxed">
                    {r.after}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <p
          className="mt-8 sm:mt-10 text-center text-[12.5px] text-[#6B6A65] max-w-2xl mx-auto"
          data-testid="vecchio-metodo-disclaimer"
        >
          Niente cifre inventate, nessuna promessa di miracoli. Solo lo stesso
          mestiere, con meno schermi aperti.
        </p>
      </div>
    </section>
  );
}
